import { RAD, clamp } from './constants.js';
import { cameraViews } from './optics.js';
import { inspectSurface } from './tasks.js';
import { recordingFor } from './recording.js';
import { threshold } from './standards.js';
import { plateCorners } from './vehiclePlates.js';
import { withBuildingVisibility } from './buildingVisibility.js';

import { projectRecordedPoint, recordedMotion } from './projection.js';
export { projectRecordedPoint } from './projection.js';

export function inspectPlate(S, plate, cameraId, moduleId) {
  const camera = S.cams.find(c => c.id === cameraId);
  if (!camera || !plate) return null;
  S = withBuildingVisibility(S);
  const views = cameraViews(S, camera), view = views.find(v => v.module?.id === moduleId) || views[0];
  if (!view) return null;
  const { cam, g } = view, exposureMs = recordingFor(S, camera).exposureMs;
  const point = { x: plate.x, y: plate.y, height: plate.heightAboveGround };
  // Let the common engine decide visibility/angles, then apply the exact
  // projected pixel width below, rather than a slant-range density estimate.
  const result = inspectSurface(S, cam, point, { req: 'plate', heading: plate.heading, width: plate.width,
    targetHeight: plate.height, minPx: 0, _single: true }, g);
  const corners = plateCorners(plate).map(p => projectRecordedPoint(cam, g, p));
  const projected = corners.every(Boolean);
  const edge = (a, b) => Math.hypot(corners[a].x - corners[b].x, corners[a].y - corners[b].y);
  const pixelWidth = projected ? Math.min(edge(0, 1), edge(3, 2)) : 0;
  const pixelHeight = projected ? Math.min(edge(0, 3), edge(1, 2)) : 0;
  const minPixels = plate.minPixels ?? threshold(S, 'plate') * plate.width;
  const blurPx = recordedMotion(cam, g, plateCorners(plate), plate.speedKph / 3.6, plate.travelHeading, exposureMs);
  let reason = result.reason;
  if (!plate.text.trim()) reason = 'No registration text configured';
  else if (g.m.pano) reason = 'Select a rectilinear or dewarped optical profile for plate inspection';
  else if (!projected) reason = 'Plate crosses or lies behind the camera near plane';
  else if (reason === 'Requirement met' && pixelWidth < minPixels) reason = 'Insufficient pixels across plate';
  else if (reason === 'Requirement met' && blurPx == null) reason = exposureMs > 0 ? 'Motion crosses the camera near plane during exposure' : 'Exposure unknown for moving vehicle';
  else if (reason === 'Requirement met' && plate.maxBlurPx > 0 && blurPx > plate.maxBlurPx) reason = 'Estimated motion blur exceeds allowance';
  const bounds = projected ? { left: Math.min(...corners.map(p => p.x)), right: Math.max(...corners.map(p => p.x)),
    top: Math.min(...corners.map(p => p.y)), bottom: Math.max(...corners.map(p => p.y)) } : null;
  const crop = bounds && bounds.right > 0 && bounds.bottom > 0 && bounds.left < g.m.resW && bounds.top < g.m.resH ? (() => {
    // A bounded sensor window avoids allocating a whole high-resolution frame.
    const width = Math.min(g.m.resW, 1024, Math.max(160, Math.ceil(bounds.right - bounds.left) + 48));
    const height = Math.min(g.m.resH, 512, Math.max(96, Math.ceil(bounds.bottom - bounds.top) + 48));
    return { x: Math.round(clamp((bounds.left + bounds.right - width) / 2, 0, g.m.resW - width)),
      y: Math.round(clamp((bounds.top + bounds.bottom - height) / 2, 0, g.m.resH - height)), width, height,
      limited: bounds.right - bounds.left + 48 > width || bounds.bottom - bounds.top + 48 > height };
  })() : null;
  return { ...result, reason, met: reason === 'Requirement met', pixelWidth, pixelHeight, minPixels,
    density: pixelWidth / plate.width, blurPx, exposureMs: exposureMs > 0 ? exposureMs : null,
    recordingWidth: g.m.resW, recordingHeight: g.m.resH, moduleId: view.module?.id ?? null,
    cam, g, crop, corners, plate, approximate: !!g.m.pano };
}
